import React, { createContext, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { initializeAuth, logout, setAuthData } from '../redux/Slices/globalData';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { isLoggedIn, user, userId, token } = useSelector((state) => state.globalData);

    // Initialize auth state from localStorage on app start
    useEffect(() => {
        dispatch(initializeAuth());
    }, [dispatch]);

    // Check if user is authenticated
    const isAuthenticated = () => {
        return isLoggedIn && token && userId;
    };

    // Login function
    const login = (authData) => {
        const { user, userId, token } = authData;
        dispatch(setAuthData({ user, userId, token, isLoggedIn: true }));

        // Store in localStorage
        localStorage.setItem('user-token', token);
        localStorage.setItem('user-id', userId);
        if (user) {
            localStorage.setItem('user-data', JSON.stringify(user));
        }
    };

    // Logout function
    const logoutUser = () => {
        dispatch(logout());
        // Additional cleanup if needed
        localStorage.removeItem('user-data');
    };

    // Get user data
    const getUser = () => {
        if (user) return user;

        // Try to get from localStorage if not in Redux state
        const storedUser = localStorage.getItem('user-data');
        if (storedUser) {
            try {
                return JSON.parse(storedUser);
            } catch (error) {
                console.error('Error parsing user data from localStorage:', error);
                return null;
            }
        }
        return null;
    };

    // Get token
    const getToken = () => {
        return token || localStorage.getItem('user-token');
    };

    // Get user ID
    const getUserId = () => {
        return userId || localStorage.getItem('user-id');
    };

    const value = {
        isLoggedIn: isAuthenticated(),
        user: getUser(),
        userId: getUserId(),
        token: getToken(),
        login,
        logout: logoutUser,
        isAuthenticated,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
